import React, {useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {connect} from 'react-redux';
import {Column, Row} from '../../../../components/layout';
import RegisterHeader from '../../../../components/headers/register-header';
import Header1 from '../../../../components/headers/header1';
import Header2 from '../../../../components/headers/header2';
import AppButton from '../../../../components/app-button';
import AppSpinner from '../../../../components/app-spinner';
import AppTextInput from '../../../../components/input/app-text-input';
import NewErrorMessage from '../../../../components/new-error-message';
import {Icons} from '../../../../util/icons';
import colors from '../../../../util/colors';
import * as fonts from '../../../../util/fonts';
import {censorEmail, censorPhone} from '../../../../util/utility';
import {strings} from '../../../../util/strings';
import {get, post} from '../../../../api/api';
import {uriConfirmActivationCode, uriRequestActivationCode} from '../../../../api/endpoints';
import {addToWizard} from '../../../../store/actions';
import './styles.scss';

interface CodeValidationProps {
  wizard: any;
  labels?: typeof strings.pt.register;
  messageLabels?: typeof strings.pt.messages;
  globalStyles: any;
  addToWizard?: (value: any) => void;
}

const CodeValidation = ({
  wizard,
  labels,
  messageLabels,
  globalStyles,
  addToWizard,
}: CodeValidationProps) => {
  const {type} = useParams<{type?: string}>();
  const history = useHistory();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isSms = (type || 'sms').toLowerCase() === 'sms';
  const primaryColor = globalStyles?.primaryColor || colors.blue;

  const contact = isSms
    ? wizard?.dataValidation?.currentUser?.phone ||
      wizard?.dataValidation?.selectedContact ||
      ''
    : wizard?.dataValidation?.isNewEmail
    ? wizard?.dataValidation?.newEmail ||
      wizard?.dataValidation?.selectedContact ||
      ''
    : wizard?.dataValidation?.currentUser?.email ||
      wizard?.dataValidation?.selectedContact ||
      '';

  const handleConfirmCode = async () => {
    if (loading || resending) return;

    const fullCode = code.trim();

    if (!fullCode) {
      setErrorMessage(
        messageLabels?.requiredField ||
          strings.pt.messages.requiredField,
      );
      return;
    }

    setErrorMessage(null);

    const sessionId =
      wizard?.dataValidation?.currentUser?.sessionId ||
      wizard?.activateRegistration?.guid ||
      '';

    try {
      setLoading(true);
      const res: any = await get(
        uriConfirmActivationCode(fullCode, sessionId),
        false,
        0,
        'Register-CodeValidation',
      );

      setLoading(false);

      if (
        res?.result?.status === 'Success' ||
        res === true ||
        res?.status === 200 ||
        !res?.error
      ) {
        if (addToWizard) {
          addToWizard({
            dataValidation: {
              ...wizard?.dataValidation,
              validationType: isSms ? 'SMS' : 'EMAIL',
              useSessionPhone: isSms,
              useSessionEmail: !isSms && !wizard?.dataValidation?.isNewEmail,
            },
          });
        }
        if (isSms) {
          history.push('/registo/novo-email');
        } else if (wizard?.dataValidation?.isNewEmail) {
          history.push('/registo/definir-palavra-passe');
        } else {
          history.push('/registo/associar-email');
        }
      } else {
        setErrorMessage(
          res?.result?.message ||
            labels?.register_wrongActivationCode ||
            strings.pt.register.register_wrongActivationCode,
        );
      }
    } catch (error: any) {
      setLoading(false);
      const errorMsg =
        error?.response?.data?.result?.message ||
        error?.message ||
        labels?.register_wrongActivationCode ||
        strings.pt.register.register_wrongActivationCode;
      setErrorMessage(errorMsg);
    }
  };

  const handleResendCode = async () => {
    setErrorMessage(null);

    const sessionId =
      wizard?.dataValidation?.currentUser?.sessionId ||
      wizard?.activateRegistration?.guid ||
      '';

    const fullName = wizard?.insuranceValidation?.name || '';

    const payload: any = {
      sessionId: sessionId,
      senderType: isSms ? 'SMS' : 'EMAIL',
      name: fullName,
      ...(wizard?.dataValidation?.isNewEmail && {
        destination:
          wizard?.dataValidation?.newEmail ||
          wizard?.dataValidation?.selectedContact,
      }),
    };

    try {
      setResending(true);
      const res: any = await post(
        uriRequestActivationCode(),
        JSON.stringify(payload),
        false,
        'Register-CodeValidation-Resend',
      );

      setResending(false);

      if (
        res?.result?.status === 'Success' ||
        res?.status === 200 ||
        !res?.error
      ) {
        setCode('');
      } else {
        setErrorMessage(
          labels?.register_phone_activation_resend_error ||
            strings.pt.register.register_phone_activation_resend_error ||
            res?.result?.message ||
            messageLabels?.general_errorMessage,
        );
      }
    } catch (error: any) {
      setResending(false);
      const errorMsg =
        labels?.register_phone_activation_resend_error ||
        strings.pt.register.register_phone_activation_resend_error ||
        error?.response?.data?.result?.message ||
        error?.message ||
        messageLabels?.general_errorMessage;
      setErrorMessage(errorMsg);
    }
  };

  return (
    <Column className="register-main-column">
      <RegisterHeader />
      <Column className="register-container" style={{ alignItems: 'center' }}>
        <Row
          className="register-header-row"
          style={{ justifyContent: 'center', marginTop: '2.5rem' }}
        >
          <Header1
            style={{
              ...fonts.XL,
              ...fonts.Bold,
              color: colors.black,
              textAlign: 'center',
            }}
          >
            {isSms
              ? labels?.register_phone_activation_title ||
                strings.pt.register.register_phone_activation_title
              : labels?.register_email_activation_title ||
                strings.pt.register.register_email_activation_title}
          </Header1>
        </Row>

        <Column
          className="register-existing-user-text"
          style={{
            ...fonts.ML,
            alignItems: 'center',
            textAlign: 'center',
            maxWidth: '380px',
            width: '100%',
          }}
        >
          <Header2
            style={{
              ...fonts.M,
              marginBottom: '2.5rem',
              textAlign: 'center',
              color: colors.darkGrey,
              lineHeight: '1.4',
            }}
          >
            {isSms
              ? `${
                  labels?.register_phone_activation_subtitle_code ||
                  strings.pt.register.register_phone_activation_subtitle_code
                } ${censorPhone(contact)}.`
              : `${
                  labels?.register_email_activation_subtitle_code ||
                  strings.pt.register.register_email_activation_subtitle_code
                } ${censorEmail(contact)}.`}
          </Header2>

          {errorMessage && (
            <Row
              style={{
                width: '100%',
                marginBottom: '1.5rem',
                justifyContent: 'center',
              }}
            >
              <NewErrorMessage
                iconName={Icons.ALERT_PROBLEM}
                colorIcon={colors.alertRed}
                text={errorMessage}
              />
            </Row>
          )}

          <Row style={{ width: '100%', marginBottom: '2rem' }}>
            <AppTextInput
              value={code}
              onChange={(value: string) => setCode(value)}
              placeholder={
                labels?.activation_activationCodePlaceholder ||
                strings.pt.register.activation_activationCodePlaceholder
              }
              type="text"
              style={{ ...fonts.M, width: '100%' }}
              disabled={loading || resending}
            />
          </Row>

          <div
            className="code-validation-resend"
            style={{
              ...fonts.M,
              ...fonts.Regular,
              color: colors.darkGrey,
              marginBottom: '2.5rem',
            }}
          >
            <span>
              {labels?.register_phone_activation_code_question ||
                strings.pt.register.register_phone_activation_code_question}{' '}
            </span>
            {resending ? (
              <AppSpinner type="grey" />
            ) : (
              <a
                href="#"
                className="code-validation-link"
                style={{
                  ...fonts.M,
                  ...fonts.SemiBold,
                  color: primaryColor,
                  textDecoration: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  pointerEvents: loading ? 'none' : 'auto',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  if (!loading && !resending) {
                    handleResendCode();
                  }
                }}
              >
                {labels?.activation_sendNewCode ||
                  strings.pt.register.activation_sendNewCode}
              </a>
            )}
          </div>

          {loading ? (
            <AppSpinner type="grey" />
          ) : (
            <AppButton
              onClick={handleConfirmCode}
              disabled={false}
              style={{
                ...fonts.M,
                ...fonts.Bold,
                width: '100%',
                maxWidth: '350px',
                borderRadius: '25px',
                height: '50px',
                backgroundColor: primaryColor,
                color: colors.white,
                cursor: 'pointer',
              }}
            >
              {labels?.insuranceAndPhone_confirmButtonText ||
                strings.pt.register.insuranceAndPhone_confirmButtonText}
            </AppButton>
          )}
        </Column>
      </Column>
    </Column>
  );
};

const mapStateToProps = ({stringsDefault, wizard, globalStyles}: any) => ({
  labels: stringsDefault?.value?.pt?.register,
  messageLabels: stringsDefault?.value?.pt?.messages,
  wizard: wizard?.value,
  globalStyles: globalStyles?.value,
});

const mapDispatchToProps = (dispatch: any) => ({
  addToWizard: (value: any) => dispatch(addToWizard(value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CodeValidation);
